import { createAuthClient } from 'better-auth/react';
import { useCallback, useEffect, useState } from 'react';

export const authClient = createAuthClient({ basePath: '/api/auth' });

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

/** Stable, privacy-preserving browser fingerprint hash (coarse device traits only; no canvas or audio probing). */
let fingerprintPromise: Promise<string> | null = null;
export function deviceFingerprint(): Promise<string> {
  fingerprintPromise ??= (async () => {
    const n = navigator as Navigator & { deviceMemory?: number };
    const parts = [
      n.userAgent,
      n.language,
      n.platform,
      String(n.hardwareConcurrency ?? ''),
      String(n.deviceMemory ?? ''),
      `${screen.width}x${screen.height}x${screen.colorDepth}`,
      String(window.devicePixelRatio),
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      String(n.maxTouchPoints ?? ''),
    ].join('|');
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(parts));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  })();
  return fingerprintPromise;
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const res = await fetch(`/api/v1${path}`, {
    ...rest,
    headers: {
      ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
      'x-device-fingerprint': await deviceFingerprint(),
      ...(rest.headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
    credentials: 'same-origin',
  });
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => ({}))) as {
    error?: { code: string; message: string; details?: unknown };
  };
  if (!res.ok)
    throw new ApiError(
      body.error?.code ?? 'internal',
      body.error?.message ?? 'Request failed',
      res.status,
      body.error?.details,
    );
  return body as T;
}

export async function adminApi<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const res = await fetch(`/api/admin${path}`, {
    ...rest,
    headers: {
      ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(rest.headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
    credentials: 'same-origin',
  });
  const body = (await res.json().catch(() => ({}))) as { error?: { code: string; message: string } };
  if (!res.ok)
    throw new ApiError(body.error?.code ?? 'internal', body.error?.message ?? 'Request failed', res.status);
  return body as T;
}

/** Tiny data hook: load on mount (and when `key` changes), expose reload. */
export function useApi<T>(path: string | null, key: unknown = path) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const load = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      setData(await api<T>(path));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError('internal', String(e), 0));
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => {
    void load();
  }, [load, key]);
  return { data, error, loading, reload: load };
}

export interface Projection {
  monthToDateMicro: number;
  dailyRateMicro: number;
  projectedMonthMicro: number;
  daysElapsed: number;
  daysRemaining: number;
  sampleDays: number;
}
export interface Forecast {
  generatedAt: string;
  account: Projection & {
    balanceMicro: number;
    daysOfCredit: number | null;
    creditRunsOutAt: string | null;
    shortfallMicro: number;
  };
  databases: Record<string, Projection>;
}

export function formatMoney(micro: number, precise = false): string {
  const sign = micro < 0 ? '-' : '';
  const abs = Math.abs(micro);
  if (precise || (abs > 0 && abs < 10_000))
    return `${sign}$${(abs / 1_000_000)
      .toFixed(abs < 10_000 ? 6 : 4)
      .replace(/0+$/, '')
      .replace(/\.$/, '.00')}`;
  return `${sign}$${(abs / 1_000_000).toFixed(2)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1_000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${(bytes / 1_000).toFixed(1)} kB`;
  if (bytes < 1_000_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${(bytes / 1_000_000_000).toFixed(2)} GB`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}
